import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarDays, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import CustomDropdown from './CustomDropdown';

export const DATE_RANGE_PRESETS = [
  { id: '7d', label: 'Last 7 days', days: 7 },
  { id: '30d', label: 'Last 30 days', days: 30 },
  { id: '90d', label: 'Last 90 days', days: 90 },
];

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const YEARS = Array.from({ length: 201 }, (_, index) => 2100 - index);
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_MS = 24 * 60 * 60 * 1000;

const toKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const fromKey = (value) => {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value) => {
  const date = fromKey(value);
  if (!date) return '';
  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const rangeIsValid = (start, end) => {
  const startDate = fromKey(start);
  const endDate = fromKey(end);
  return Boolean(startDate && endDate && endDate >= startDate);
};

const addDays = (date, amount) => {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
};

const getDefaultViewDate = (start, end) =>
  fromKey(end) || fromKey(start) || new Date();

const getCalendarDays = (viewDate) => {
  const first = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
  const mondayOffset = (first.getDay() + 6) % 7;
  const firstCell = addDays(first, -mondayOffset);
  return Array.from({ length: 42 }, (_, index) => addDays(firstCell, index));
};

function displayLabel(range, start, end) {
  if (range === 'custom') {
    if (start && end) return `${formatDate(start)} – ${formatDate(end)}`;
    if (start) return `${formatDate(start)} – Select end`;
    return 'Custom dates';
  }
  return (
    DATE_RANGE_PRESETS.find((preset) => preset.id === range)?.label ||
    'Last 30 days'
  );
}

export default function DateRangePicker({
  range,
  startDate,
  endDate,
  onChange,
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(range === 'custom' ? 'custom' : 'preset');
  const [draftStart, setDraftStart] = useState(startDate || '');
  const [draftEnd, setDraftEnd] = useState(endDate || '');
  const [viewDate, setViewDate] = useState(() =>
    getDefaultViewDate(startDate, endDate),
  );
  const rootRef = useRef(null);
  const popoverRef = useRef(null);
  const [popoverPosition, setPopoverPosition] = useState(null);

  const calendarDays = useMemo(() => getCalendarDays(viewDate), [viewDate]);
  const currentMonth = viewDate.getMonth();
  const currentYear = viewDate.getFullYear();
  const customRangeValid = rangeIsValid(draftStart, draftEnd);

  const updatePopoverPosition = () => {
    if (!rootRef.current || typeof window === 'undefined') return;

    const trigger = rootRef.current.getBoundingClientRect();
    const width = Math.min(352, window.innerWidth - 16);
    const gap = 8;
    const estimatedHeight = mode === 'custom' ? 620 : 250;
    const maxLeft = Math.max(8, window.innerWidth - width - 8);
    const left = Math.min(Math.max(8, trigger.right - width), maxLeft);
    let top = trigger.bottom + gap;

    if (
      top + estimatedHeight > window.innerHeight - 8 &&
      trigger.top - estimatedHeight - gap >= 8
    ) {
      top = trigger.top - estimatedHeight - gap;
    }

    setPopoverPosition({
      top: Math.max(8, top),
      left,
      width,
      maxHeight: Math.max(240, window.innerHeight - 16),
    });
  };

  useEffect(() => {
    const handlePointerDown = (event) => {
      const clickedTrigger = rootRef.current?.contains(event.target);
      const clickedPopover = popoverRef.current?.contains(event.target);
      if (!clickedTrigger && !clickedPopover) setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!open) {
      setPopoverPosition(null);
      return undefined;
    }

    updatePopoverPosition();
    const handleViewportChange = () => updatePopoverPosition();
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);
    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [open, mode]);

  const openPicker = () => {
    setDraftStart(startDate || '');
    setDraftEnd(endDate || '');
    setMode(range === 'custom' ? 'custom' : 'preset');
    setViewDate(getDefaultViewDate(startDate, endDate));
    setOpen((current) => !current);
  };

  const selectPreset = (preset) => {
    const end = toKey(new Date());
    const start = toKey(addDays(new Date(), -(preset.days - 1)));
    onChange({ id: preset.id, start, end });
    setOpen(false);
  };

  const selectDay = (dayKey) => {
    if (!draftStart || (draftStart && draftEnd)) {
      setDraftStart(dayKey);
      setDraftEnd('');
      return;
    }

    if (dayKey < draftStart) {
      setDraftStart(dayKey);
      setDraftEnd('');
      return;
    }

    setDraftEnd(dayKey);
  };

  const applyCustomRange = () => {
    if (!customRangeValid) return;
    onChange({ id: 'custom', start: draftStart, end: draftEnd });
    setOpen(false);
  };

  const goToMonth = (amount) => {
    setViewDate(new Date(currentYear, currentMonth + amount, 1));
  };

  return (
    <div ref={rootRef} className="relative w-full sm:w-auto">
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Choose date range"
        onClick={openPicker}
        className="inline-flex h-8 w-full items-center gap-2 rounded-lg border border-slate-300 bg-white px-2.5 text-left text-[11px] font-semibold text-slate-700 shadow-sm outline-none transition hover:border-slate-400 focus:border-brand focus:ring-2 focus:ring-brand/10 sm:w-[176px]"
      >
        <CalendarDays size={13} className="shrink-0 text-brand" />
        <span className="min-w-0 flex-1 truncate">
          {displayLabel(range, startDate, endDate)}
        </span>
        <ChevronRight
          size={13}
          className={`shrink-0 rotate-90 text-slate-400 transition-transform duration-200 ${
            open ? 'rotate-[270deg] text-brand' : ''
          }`}
        />
      </button>

      {open &&
        popoverPosition &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={popoverRef}
            style={{
              top: popoverPosition.top,
              left: popoverPosition.left,
              width: popoverPosition.width,
              maxHeight: popoverPosition.maxHeight,
            }}
            className="fixed z-[9999] overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-[0_18px_48px_-18px_rgba(15,23,42,0.32)]"
          >
            <div className="border-b border-slate-100 px-4 pb-3 pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-800">
                    Date range
                  </p>
                  <p className="mt-0.5 text-[11px] text-slate-400">
                    Choose a preset or select any month and year.
                  </p>
                </div>
                <CalendarDays size={17} className="text-brand" />
              </div>

              <div className="mt-3 grid grid-cols-2 gap-1.5">
                {DATE_RANGE_PRESETS.map((preset) => {
                  const isSelected = range === preset.id;
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => selectPreset(preset)}
                      className={`rounded-lg px-2.5 py-2 text-left text-xs font-semibold transition-colors ${
                        isSelected
                          ? 'bg-brand-soft text-brand'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      }`}
                    >
                      {preset.label}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => setMode('custom')}
                  className={`rounded-lg px-2.5 py-2 text-left text-xs font-semibold transition-colors ${
                    mode === 'custom'
                      ? 'bg-brand-soft text-brand'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  Custom dates
                </button>
              </div>
            </div>

            {mode === 'custom' && (
              <div className="p-4">
                <div className="mb-3 flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2 text-[11px]">
                  <span className="font-medium text-slate-500">
                    {draftStart ? formatDate(draftStart) : 'Start date'}
                  </span>
                  <span className="text-slate-300">→</span>
                  <span className="font-medium text-slate-500">
                    {draftEnd ? formatDate(draftEnd) : 'End date'}
                  </span>
                </div>

                <div className="mb-3 flex items-center gap-2">
                  <CustomDropdown
                    value={String(currentMonth)}
                    options={MONTHS.map((month, index) => ({
                      value: String(index),
                      label: month,
                    }))}
                    onChange={(month) =>
                      setViewDate(new Date(currentYear, Number(month), 1))
                    }
                    ariaLabel="Select month"
                    className="min-w-0 flex-1"
                    buttonClassName="px-2.5"
                    menuClassName="left-0 right-auto max-h-60 overflow-y-auto"
                  />
                  <CustomDropdown
                    value={String(currentYear)}
                    options={YEARS.map((year) => ({
                      value: String(year),
                      label: String(year),
                    }))}
                    onChange={(year) =>
                      setViewDate(new Date(Number(year), currentMonth, 1))
                    }
                    ariaLabel="Select year"
                    className="w-[92px] shrink-0"
                    buttonClassName="px-2.5"
                    menuClassName="left-auto right-0 max-h-60 overflow-y-auto"
                  />
                </div>

                <div className="mb-2 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => goToMonth(-1)}
                    aria-label="Previous month"
                    className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
                  >
                    <ChevronLeft size={15} />
                  </button>
                  <p className="text-xs font-semibold text-slate-700">
                    {MONTHS[currentMonth]} {currentYear}
                  </p>
                  <button
                    type="button"
                    onClick={() => goToMonth(1)}
                    aria-label="Next month"
                    className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
                  >
                    <ChevronRight size={15} />
                  </button>
                </div>

                <div className="grid grid-cols-7 gap-1 text-center">
                  {DAY_NAMES.map((day) => (
                    <span
                      key={day}
                      className="py-1 text-[10px] font-bold uppercase tracking-wide text-slate-400"
                    >
                      {day.slice(0, 2)}
                    </span>
                  ))}
                  {calendarDays.map((day) => {
                    const dayKey = toKey(day);
                    const isCurrentMonth = day.getMonth() === currentMonth;
                    const isStart = dayKey === draftStart;
                    const isEnd = dayKey === draftEnd;
                    const isInRange =
                      draftStart &&
                      draftEnd &&
                      dayKey > draftStart &&
                      dayKey < draftEnd;
                    const isSelected = isStart || isEnd;

                    return (
                      <button
                        key={dayKey}
                        type="button"
                        onClick={() => selectDay(dayKey)}
                        className={`relative h-8 rounded-lg text-xs transition-colors ${
                          isSelected
                            ? 'bg-brand font-bold text-white shadow-sm'
                            : isInRange
                              ? 'bg-brand-soft font-semibold text-brand'
                              : isCurrentMonth
                                ? 'text-slate-700 hover:bg-slate-100'
                                : 'text-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        {day.getDate()}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-4 flex items-center justify-between gap-2 border-t border-slate-100 pt-3">
                  <button
                    type="button"
                    onClick={() => {
                      setDraftStart('');
                      setDraftEnd('');
                    }}
                    className="text-xs font-semibold text-slate-500 hover:text-slate-800"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={applyCustomRange}
                    disabled={!customRangeValid}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Check size={13} />
                    Apply dates
                  </button>
                </div>
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}
